/**
 * Invoice DTO exposed by rental-service `/api/invoices`.
 * Mirrors `net.nawam.rental.model.entity.Invoice` filtered by `IRead`.
 */

import type { Rental } from './rental.types';
import type { UserRef } from './user.types';

/** All possible invoice statuses (see `InvoiceStatusEnum`). */
export type InvoiceStatus =
  | 'PENDING'
  | 'PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'REFUNDED';

export interface Invoice {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── identity ──────────────────────────────────────────────────────────
  invoiceNumber: string;
  rental: Rental | null;
  /** Customer (renter) user id. */
  renterId?: number | null;
  /** Vehicle owner user id. */
  ownerId?: number | null;

  /** Read-model du locataire (alimenté par `@JoinColumn(referencedColumnName = "auth_user_id")`). */
  renterUser?: UserRef | null;
  /** Read-model du propriétaire du véhicule. */
  ownerUser?: UserRef | null;

  // ── dates ─────────────────────────────────────────────────────────────
  issueDate?: string | null;
  dueDate?: string | null;
  paidDate?: string | null;

  // ── financial (BigDecimal → string|number ; we use number for display) ──
  rentalAmount?: number | null;
  lateFee?: number | null;
  additionalCharges?: number | null;
  discount?: number | null;
  subtotal?: number | null;
  taxAmount?: number | null;
  totalAmount?: number | null;

  // ── caution / dépôt de garantie (#46) ──────────────────────────────────
  /** Taux de caution figé à la facturation (%). */
  depositRatePct?: number | null;
  /** Caution facturée = rentalAmount × depositRatePct/100 (en sus, hors settlement). */
  depositAmount?: number | null;
  /** Part de la caution conservée à la clôture (frais de retard + dommages). */
  depositRetained?: number | null;
  /** Reliquat de caution rendu au client. */
  depositRefund?: number | null;
  /** Statut de la caution (NONE / HELD / ...). */
  depositStatus?: string | null;
  /** Montant réellement débité = totalAmount + depositAmount (getter back). */
  amountToCollect?: number | null;
  /**
   * Id de la demande de remboursement liée (#46) — nécessaire pour « J'ai
   * remboursé la caution ». ⚠️ Non encore exposé par l'IRead back (dépendance) :
   * le bouton owner ne s'affiche que si ce champ est présent.
   */
  refundRequestId?: number | null;

  // ── status & docs ─────────────────────────────────────────────────────
  status: InvoiceStatus;
  pdfUrl?: string | null;
  notes?: string | null;
  paymentMethod?: string | null;
  paymentReference?: string | null;
}

import { defensiveStatusMeta } from '../helpers/status-meta';

/** UI helper : maps an invoice status to its label + Tailwind palette suffix.
 *  Defensive: unknown/null statuses fall back to `{label:'—', palette:'gray'}`. */
export const INVOICE_STATUS_META = defensiveStatusMeta<InvoiceStatus>({
  PENDING: { label: 'status.invoice.PENDING', palette: 'warning' },
  PAID: { label: 'status.invoice.PAID', palette: 'brand' },
  OVERDUE: { label: 'status.invoice.OVERDUE', palette: 'error' },
  CANCELLED: { label: 'status.invoice.CANCELLED', palette: 'gray' },
  REFUNDED: { label: 'status.invoice.REFUNDED', palette: 'blue-light' },
});

/** Payload for `POST /api/invoices/{id}/mark-paid`. */
export interface MarkInvoicePaidRequest {
  paymentMethod: string;
  paymentReference?: string;
}
